import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../../services/auth';

interface Visit {
  visit_id: number;
  patient_name: string;
  status: string;
  appointment_time?: string;
  doctor_name?: string;
  assigned_doctor_id?: number;
  visit_type?: string;
}

interface Doctor {
  id: number;
  name: string;
}

const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8 AM to 8 PM

const STATUS_COLORS: Record<string, string> = {
  Open: '#3b82f6', WAITING: '#f59e0b', IN_PROGRESS: '#8b5cf6',
  Completed: '#059669', Locked: '#64748b', BOOKED: '#06b6d4',
};

export default function DayTimeline() {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    axios.get(`/api/visits-by-date?date=${selectedDate}`, { headers })
      .then((r) => {
        setVisits(r.data || []);
        // Extract unique doctors
        const docs = new Map<number, string>();
        (r.data || []).forEach((v: Visit) => {
          if (v.assigned_doctor_id && v.doctor_name) docs.set(v.assigned_doctor_id, v.doctor_name);
        });
        if (docs.size === 0) docs.set(0, 'Unassigned');
        setDoctors(Array.from(docs).map(([id, name]) => ({ id, name })));
      })
      .catch(() => {});
  }, [selectedDate]);

  const getHourForVisit = (v: Visit): number => {
    if (!v.appointment_time) return 9; // Default 9 AM
    try {
      const timeStr = typeof v.appointment_time === 'string' ? v.appointment_time : '';
      const h = parseInt(timeStr.split(':')[0] || timeStr.split('T')[1]?.split(':')[0] || '9');
      return h >= 8 && h <= 20 ? h : 9;
    } catch { return 9; }
  };

  const getVisitsForSlot = (doctorId: number, hour: number) => {
    return visits.filter((v) => {
      const docMatch = doctorId === 0
        ? !v.assigned_doctor_id
        : v.assigned_doctor_id === doctorId;
      return docMatch && getHourForVisit(v) === hour;
    });
  };

  const getStatusClass = (status?: string) => {
    const key = String(status || "").trim().toUpperCase();
    switch (key) {
      case "OPEN":
        return "rc-status-open";
      case "WAITING":
        return "rc-status-waiting";
      case "IN_PROGRESS":
        return "rc-status-progress";
      case "COMPLETED":
        return "rc-status-completed";
      case "LOCKED":
        return "rc-status-locked";
      case "BOOKED":
        return "rc-status-booked";
      default:
        return "rc-status-default";
    }
  };

  return (
    <div className="rc-timeline">
      <div className="rc-timeline-header">
        <h3 className="rc-timeline-title">📅 Appointment Timeline</h3>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="rc-date-input"
        />
      </div>

      <div className="rc-timeline-table-wrap">
        <table className="rc-timeline-table">
          <thead>
            <tr>
              <th className="rc-timeline-time-col">Time</th>
              {doctors.map((d) => (
                <th key={d.id} className="rc-timeline-doc-col">
                  🩺 {d.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HOURS.map((hour) => (
              <tr key={hour} className="rc-timeline-row">
                <td className="rc-timeline-time-cell">
                  {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                </td>
                {doctors.map((d) => {
                  const slotVisits = getVisitsForSlot(d.id, hour);
                  return (
                    <td key={d.id} className="rc-timeline-cell">
                      {slotVisits.map((v) => (
                        <div
                          key={v.visit_id}
                          className={`rc-timeline-visit ${getStatusClass(v.status)}`}
                        >
                          <div className="rc-timeline-name">{v.patient_name}</div>
                          <div className="rc-timeline-status">
                            {v.status} {v.visit_type ? `• ${v.visit_type}` : ''}
                          </div>
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
